import { useCallback, useEffect, useState } from 'react'
import {
    Box,
    List,
    ListItemButton,
    ListItemText,
    Typography

} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentTheme, selectThemeMode } from '../../selectors/common'
import commonActions from '../../actions/common.actions'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import NightlightOutlinedIcon from '@mui/icons-material/NightlightOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AppearanceIcon from '../../assets/icons/AppearanceIcon'

interface AppearanceMenuProps {
    hovered: boolean
}

export function AppearanceMenu({ hovered }: AppearanceMenuProps) {
    const darkTheme = useSelector(selectCurrentTheme)
    const dispatch = useDispatch()
    const themeMode = useSelector(selectThemeMode)
    const [userSelectedMode, setUserSelectedMode] = useState<"Light" | "Dark" | "System">();

    const handleThemeChange = useCallback((mode: 'Light' | 'Dark' | 'System') => {
        setUserSelectedMode(mode);
        commonActions.setThemeMode(dispatch, mode)
        localStorage.setItem('themeMode', mode)
    }, [dispatch])

    const checkSystemTheme = useCallback(() => {
        const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
        const systemTheme = isDarkMode ? "Dark" : "Light";
        const savedTheme = localStorage.getItem("themeMode") || "Dark";
        const finalTheme = savedTheme === "System" ? systemTheme : savedTheme;
        commonActions.setThemeMode(dispatch, finalTheme as any);
    }, [dispatch]);

    useEffect(() => {
        checkSystemTheme();
    }, [checkSystemTheme]);

    useEffect(() => {
        const handleFocus = () => {
            checkSystemTheme();
        };

        window.addEventListener("focus", handleFocus);
        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, [checkSystemTheme]);
    return (
        <>
            {hovered ? (
                <List component="div" sx={{ p: 0 }}>
                    <ListItemButton
                        data-event="Theme Changed"
                        data-event-theme-selected={darkTheme ? 'Light' : 'Dark'}
                        sx={{ py: 0, height: "42px", display: "flex", justifyContent: 'start', '&:hover': { background: theme => theme.palette.background.default }, padding: '0px 10px ' }}
                    >
                        <Box>
                            <AppearanceIcon fontSize={'large'} sx={{color: (theme) => theme.palette.text.secondary}}/>
                        </Box>
                        <ListItemText sx={{ ml: "6px", whiteSpace: "nowrap" }}>
                            <Typography fontSize="13px">{themeMode}</Typography>
                        </ListItemText>

                        <ToggleButtonGroup
                            value={themeMode}
                            exclusive
                            onChange={(_, mode) => mode && handleThemeChange(mode)}
                            sx={{
                                ml: "10px",
                                height: "30px",
                                alignItems: "center",
                                '.MuiButtonBase-root': {
                                    borderColor: (theme) => theme.palette.divider,
                                },
                                '&:not(:first-of-type)': {
                                    marginLeft: (theme) => theme.spacing(1),
                                },
                            }}
                        >
                            <ToggleButton
                                value="Light"
                                selected={userSelectedMode === "Light" && themeMode !== "System"}
                                sx={{
                                    color: (theme) => themeMode === "Light" && userSelectedMode !== "System" ? theme.palette.text.primary : theme.palette.text.secondary,
                                    height: "20px",
                                    width: "26px",
                                    minWidth: "26px",
                                    borderRadius: "2px 0 0 2px",
                                }}
                            >
                                <LightModeOutlinedIcon fontSize="small" />
                            </ToggleButton>

                            <ToggleButton
                                value="Dark"
                                selected={userSelectedMode === "Dark" && themeMode !== "System"}
                                sx={{
                                    color: (theme) => themeMode === "Dark" && userSelectedMode !== "System" ? theme.palette.text.primary : theme.palette.text.secondary,
                                    height: "20px",
                                    width: "26px",
                                    minWidth: "26px",
                                    borderRadius: "2px 0 0 2px",
                                }}
                            >
                                <NightlightOutlinedIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton
                                value="System"
                                selected={userSelectedMode === "System"}
                                sx={{
                                    color: (theme) => userSelectedMode === "System" ? theme.palette.text.primary : theme.palette.text.secondary,
                                    height: "20px",
                                    width: "26px",
                                    minWidth: "26px",
                                    borderRadius: "2px 0 0 2px",
                                }}
                            >
                                <AutoFixHighOutlinedIcon fontSize="small" />
                            </ToggleButton>
                        </ToggleButtonGroup>

                    </ListItemButton>
                </List>
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '42px' }}>
                    <AppearanceIcon fontSize={'large'} sx={{color: (theme) => theme.palette.text.secondary}}/>
                </Box>
            )}
        </>
    )
}